import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ASSETS } from "../lib/brand";

const STORAGE_KEY = "haven_exit_intent_seen";

/**
 * ExitIntentModal — fires once per session when the cursor moves up toward the
 * browser tabs/address bar (a strong "I'm leaving" signal on desktop). Plays
 * Daniel's brand video and a soft, content-led CTA. NOT a discount popup.
 *
 * Skipped on touch devices (mobile users don't have a meaningful exit signal).
 */
export const ExitIntentModal = ({ source = "landing" }) => {
  const [open, setOpen] = useState(false);
  const armedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only on desktop — touch devices don't have a meaningful exit-intent.
    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (isTouch) return;

    // Once per session per source.
    let seen = false;
    try {
      seen = !!sessionStorage.getItem(STORAGE_KEY);
    } catch {
      // private mode etc.
    }
    if (seen) return;

    // Arm only after 5s on page so we don't fire on quick bounces.
    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, 5000);

    const onMouseOut = (e) => {
      if (!armedRef.current) return;
      if (e.relatedTarget) return; // moving to another element on the page
      if (e.clientY > 5) return; // only when cursor moves to the very top
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {}
      setOpen(true);
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  if (!open) return null;

  const close = () => setOpen(false);

  const lockRate = () => {
    setOpen(false);
    navigate("/join?tier=annual&utm=exit-intent");
  };

  return (
    <div
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "rgba(42, 37, 32, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "havenExitFade 220ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="haven-card"
        style={{
          width: "min(640px, 96vw)",
          maxHeight: "92vh",
          overflow: "auto",
          padding: 0,
          background: "var(--warm-white)",
          borderRadius: 22,
          border: "1px solid var(--gold-light)",
          boxShadow: "0 30px 80px -30px rgba(42,37,32,0.5)",
          animation: "havenExitRise 280ms cubic-bezier(.2,.7,.2,1)",
        }}
      >
        {/* Close */}
        <button
          data-testid="exit-intent-close"
          aria-label="Close"
          onClick={close}
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "1px solid var(--border-soft)",
            background: "var(--surface-card)",
            color: "var(--ink-soft)",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: "30px",
            zIndex: 2,
          }}
        >
          ×
        </button>

        {/* Video — Daniel's "leaving so soon?" message, embedded from YouTube */}
        <div
          style={{
            position: "relative",
            background: "var(--ink)",
            width: "100%",
            paddingTop: "56.25%", // 16:9 aspect ratio
          }}
        >
          <iframe
            data-testid="exit-intent-video"
            src={`https://www.youtube.com/embed/${ASSETS.exitIntentYouTubeId}?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1`}
            title="Daniel — a short message"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        </div>

        {/* Body */}
        <div style={{ padding: "26px 28px 28px" }}>
          <span className="haven-label">Before you go</span>
          <h2
            id="exit-intent-title"
            className="font-serif"
            style={{ fontSize: "clamp(26px, 3.6vw, 36px)", lineHeight: 1.1, color: "var(--ink)", marginTop: 6 }}
          >
            Watch this first.
            <br />
            <em style={{ color: "var(--gold)" }}>Then decide.</em>
          </h2>
          <p style={{ marginTop: 10, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.65 }}>
            If you've made it this far you're already curious. Daniel made a short message for you — about
            why he's funding this with founders instead of investors, and what that actually means for the
            way the curriculum gets built.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              data-testid="exit-intent-cta-join"
              onClick={lockRate}
              className="haven-btn haven-btn-gold"
            >
              Lock founding rate
            </button>
            <Link
              to="/diagnose"
              data-testid="exit-intent-cta-diagnose"
              onClick={close}
              className="haven-btn haven-btn-ghost"
            >
              Try the diagnostic free
            </Link>
            <button
              data-testid="exit-intent-cta-stay"
              onClick={close}
              className="haven-btn haven-btn-ghost"
              style={{ borderColor: "transparent", color: "var(--ink-muted)" }}
            >
              Keep browsing
            </button>
          </div>
          <div
            style={{
              marginTop: 18,
              borderTop: "1px solid var(--border-soft)",
              paddingTop: 12,
              fontSize: 11,
              color: "var(--ink-muted)",
              letterSpacing: "0.04em",
            }}
          >
            Member-funded · No outside investors · {source === "join" ? "You're already on the founding page." : "500 founding seats."}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes havenExitFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes havenExitRise {
          from { opacity: 0; transform: translateY(14px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ExitIntentModal;
